import {createEntityAdapter, EntityAdapter, EntityState} from "@ngrx/entity";
import {Action, createReducer, on} from "@ngrx/store";
import {PageEvent} from "@angular/material/paginator";
import {Sort} from "@angular/material/sort";

import * as TrunkNoticeActions from "./trunk-notice.actions";
import {TrunkNotice} from "@knust/api-interfaces";

export const TRUNK_NOTICE_FEATURE_KEY = 'trunkNotice';

export interface State extends EntityState<TrunkNotice> {
  selectedId?: number; // which TrunkNotice record has been selected
  loaded: boolean; // has the TrunkNotice list been loaded
  error?: string | null; // last known error (if any)
  total: number;
  page: PageEvent;
  term: string;
  sort: Sort | null;
}

export interface TrunkNoticePartialState {
  readonly [TRUNK_NOTICE_FEATURE_KEY]: State;
}

export const trunkNoticeAdapter: EntityAdapter<TrunkNotice> =
  createEntityAdapter<TrunkNotice>();

const initialPaginationState = {
  total: 0,
  page: {
    pageIndex: 0,
    pageSize: 25,
    length: 0
  },
  term: '',
  sort: null
};

export const initialState: State = trunkNoticeAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  ...initialPaginationState
});

const trunkNoticeReducer = createReducer(
  initialState,
  on(TrunkNoticeActions.loadTrunkNotices, (state) => ({ ...state, loaded: false, error: null })),
  on(TrunkNoticeActions.loadTrunkNoticesSuccess, (state, { trunkNotice, total }) =>
    trunkNoticeAdapter.setAll(trunkNotice, { ...state, loaded: true, total })
  ),
  on(TrunkNoticeActions.loadTrunkNoticesFailure, (state, { error }) => ({ ...state, error })),
  on(TrunkNoticeActions.setSelectedTrunkNotice, (state, { id }) => ({ ...state, selectedId: id })),
  on(TrunkNoticeActions.loadTrunkNoticeDetailSuccess, (state, { trunkNotice }) =>
    trunkNoticeAdapter.setAll([trunkNotice], { ...state, loaded: false, ...initialPaginationState })
  ),
  on(TrunkNoticeActions.loadTrunkNoticeDetailFailure, (state, { error }) => ({ ...state, error })),
  on(TrunkNoticeActions.setTrunkNoticePage, (state, { page }) => ({ ...state, page })),
  on(TrunkNoticeActions.setTrunkNoticeTerm, (state, { term }) => ({
    ...state,
    term,
    page: {
      ...state.page,
      pageIndex: 0,
    }
  })),
  on(TrunkNoticeActions.setTrunkNoticeSort, (state, { sort }) => ({ ...state, sort })),
  on(TrunkNoticeActions.clearTrunkNotice, (state) =>
    trunkNoticeAdapter.removeAll({ ...state, ...initialPaginationState })
  ),
  on(TrunkNoticeActions.saveTrunkNoticeSuccess, (state, { trunkNotice, insert }) =>
    trunkNoticeAdapter.upsertOne(trunkNotice, { ...state, total: (insert ? state.total + 1 : state.total) })
  ),
  on(TrunkNoticeActions.saveTrunkNoticeFailure, (state, { error }) => ({ ...state, error })),
  on(TrunkNoticeActions.deleteTrunkNoticeSuccess, (state, { id }) =>
    trunkNoticeAdapter.removeOne(id + '', { ...state, total: state.total - 1 })
  ),
  on(TrunkNoticeActions.deleteTrunkNoticeFailure, (state, { error }) => ({ ...state, error })),
  on(TrunkNoticeActions.restoreTrunkNoticeSuccess, (state, { trunkNotice }) =>
    trunkNoticeAdapter.removeOne(trunkNotice.id, { ...state, total: state.total - 1 })
  ),
  on(TrunkNoticeActions.restoreTrunkNoticeFailure, (state, { error }) => ({ ...state, error }))
);

export function reducer(state: State | undefined, action: Action) {
  return trunkNoticeReducer(state, action);
}
